import type { ComponentType, ReactNode } from 'react';

/**
 * Upstream-only modules: src/modules/<name>/index.tsx, listed in .upstream-only.
 * A module is in the bundle because its folder exists; children have none.
 * See docs/superpowers/specs/2026-09-25-upstream-only-modules-design.md.
 */

export interface ModuleRoute {
  /** Without a leading slash: "canary", "canary/products/:id" */
  path: string;
  /** The page, lazy() inside the module; routes.tsx adds the Suspense */
  element: ReactNode;
  roles?: string[];
  /** A page of its own: signed-in only, but outside the app layout and its sidebar */
  standalone?: boolean;
}

export interface ModuleNavItem {
  path: string;
  labelKey: string;
  defaultLabel: string;
  icon: ComponentType<{ className?: string }>;
  roles?: string[];
  qaId: string;
}

export interface ModuleManifest {
  routes: ModuleRoute[];
  navItems: ModuleNavItem[];
}

export function collectModules(found: Record<string, { default: ModuleManifest }>) {
  const manifests = Object.keys(found)
    .sort()
    .map((key) => found[key].default);
  const routes = manifests.flatMap((manifest) => manifest.routes);
  const moduleNavItems = manifests.flatMap((manifest) => manifest.navItems);
  return {
    /** Rendered inside the app layout */
    moduleRoutes: routes.filter((route) => !route.standalone),
    /** Rendered on their own, see ModuleRoute.standalone */
    standaloneModuleRoutes: routes.filter((route) => route.standalone),
    moduleNavItems,
    // Spread into AppLayout's path → qa-id maps, so its render code stays as is
    moduleQaIds: Object.fromEntries(moduleNavItems.map((item) => [item.path, item.qaId])) as Record<string, string>,
  };
}
